<template>
  <v-card outlined color="grey lighten-4" tile class="ml-3 mt-0 mb-0">
    <v-card-text class="pa-0 ma-0">
      <DocumentListToolbar
        :totalResults="filteredSortedDocumentList.length"
        :filteredResults="
          documentList.length - filteredSortedDocumentList.length
        "
        @on-refresh-click="refreshResults"
        @on-select-all-change="selectAllDocuments"
        @on-validate-click="onValidate"
        @on-refuse-click="onRefuse"
        @on-filter-click="onFilter"
        @on-groupBy-click="onGroup"
        @on-sort-click="onSort"
        @on-export-selected-click="onExportSelected"
        @on-delete-selected-click="onDeleteSelected"
        @on-internal-distribute-selected-click="onInternalDistributeSelected"
        @on-external-distribute-selected-click="onExternalDistributeSelected"
        @on-edit-selected-click="onEditSelected"
        @on-download-selected-click="onDownloadSelected"
        @on-load-plugin-click="loadPlugin"
        :selectedDocuments="selectedDocuments"
      />
      <v-container class="blue white--text">{{ searchName }}</v-container>
      <v-row justify="center" align="center" v-if="loading">
        <div class="text-center">
          <div>
            <v-progress-circular
              class="my-4"
              :size="50"
              width="5"
              color="primary"
              indeterminate
            ></v-progress-circular>
          </div>
          <p class="display-1">{{ $t("Loading") }}...</p>
        </div>
      </v-row>
      <v-card-text class="ma-0 pa-0 overflow-y-auto" :style="styleMaxHeight">
        <base-no-data
          v-if="filteredSortedDocumentList.length == 0 && !loading"
          :text="$t('NoDocumentsFound')"
          icon="mdi-file-document-outline"
        />

        <template v-if="!loading">
          <VueAutoVirtualScrollList
            :totalHeight="resultMaxHeight"
            :defaultHeight="190"
            style="width: 100%"
          >
            <div v-if="groupedDocumentList && groupedDocumentList.length > 0">
              <v-expansion-panels multiple accordion flat tile>
                <GroupedDocumentItem
                  v-for="groupedItem in groupedDocumentList"
                  v-bind:key="groupedItem.title"
                  :groupedItem="groupedItem"
                  :selectedDocument="selectedDocument"
                  :defaultVisibleFields="defaultVisibleFields"
                  :selectedDocuments="selectedDocuments"
                  @on-version-change="reloadDocument"
                  @on-selection-change="onDocumentSelected"
                  @on-version-removed="onVersionDeleted"
                  @on-document-removed="removeDocumentFromList"
                  @on-document-blocked="onDocumentBlocked"
                  @on-reception-confirmed="onReceptionConfirmed"
                  @on-distribution-ended="onDistributionEnded"
                  @on-edit-click="editDocument"
                  @on-flow-diagram-click="showFlowDiagram"
                  @on-sign-click="signDocument"
                  @on-load-plugin-click="loadPlugin"
                  @on-content-click="loadPdf"
                />
              </v-expansion-panels>
            </div>
            <div
              v-for="document in ungroupedDocumentList"
              v-bind:key="document.ObjectID"
            >
              <DocumentItem
                :documentDto="document"
                :isSelected="document.ObjectID == selectedDocument"
                :fieldsCount="defaultVisibleFields"
                @on-version-change="reloadDocument"
                @on-selection-change="onDocumentSelected"
                @on-version-removed="onVersionDeleted"
                @on-document-removed="removeDocumentFromList"
                @on-document-blocked="onDocumentBlocked"
                @on-reception-confirmed="onReceptionConfirmed"
                @on-distribution-ended="onDistributionEnded"
                @on-edit-click="editDocument"
                @on-flow-diagram-click="showFlowDiagram"
                @on-sign-click="signDocument"
                @on-load-plugin-click="loadPlugin"
                @on-content-click="loadPdf"
                :selectedDocuments="selectedDocuments"
              />
            </div>
          </VueAutoVirtualScrollList>
        </template>
      </v-card-text>
    </v-card-text>
    <GroupModal
      :ref="groupModalKey"
      :fieldList="filteredFieldList"
      @on-grouped="onGrouped"
    />
    <FilterModal
      :ref="filterModalKey"
      :fieldList="fieldList"
      @on-filtered="onFiltered"
    />
    <SortModal
      :ref="sortModalKey"
      :fieldList="filteredFieldList"
      @on-sorted="onSorted"
    />
    <DistributionModal
      :ref="distributionModalKey"
      @on-distributed="refreshResults"
    />
    <ValidationModal :ref="validationModalKey" @on-validated="refreshResults" />
    <DocumentModal
      :ref="documentModalKey"
      @on-document-updated="onDocumentUpdated"
    />
    <MultipleEditionModal :ref="multipleEditionModalKey" />
    <SignatureModal :ref="signatureModalKey" @on-sign="onSign" />
    <AttachmentModal
      :ref="attachmentModalKey"
      @on-attachment-saved="onAttachmentSaved"
    />
    <FlowDiagramModal :ref="flowDiagramModalKey" />
    <PluginLoaderModal
      :ref="pluginLoaderModalKey"
      @on-refresh-results="refreshResults"
    />
  </v-card>
</template>

<script>
import DocumentListToolbar from "@/components/search/results/list/DocumentListToolbar";
import DocumentItem from "@/components/search/results/list/item/DocumentItem";
import GroupModal from "@/components/search/results/GroupModal.vue";
import FilterModal from "@/components/search/results/FilterModal.vue";
import SortModal from "@/components/search/results/SortModal.vue";
import DistributionModal from "@/components/search/results/DistributionModal.vue";
import ValidationModal from "@/components/search/results/ValidationModal.vue";
import PluginLoaderModal from "@/components/search/results/PluginLoaderModal.vue";
import StandardFieldCode from "@/ecm/variables/StandardFieldCode";
import FieldType from "@/ecm/variables/FieldType";
import SearchType from "@/ecm/variables/SearchType";
import SearchService from "@/services/SearchService";
import DocumentService from "@/services/DocumentService";
import Utils from "@/ecm/common/Utils";
import { EventBus } from "@/eventBus.js";
import DocumentModal from "@/components/document/documentModal/DocumentModal";
import MultipleEditionModal from "@/components/document/documentModal/MultipleEditionModal";
import AttachmentModal from "@/components/search/results/list/item/actions/attachment/AttachmentModal";
import FlowDiagramModal from "@/components/administration/flow/FlowDiagramModal.vue";
import SignatureModal from "@/components/search/results/list/item/actions/signature/SignatureModal";
import VueAutoVirtualScrollList from "vue-auto-virtual-scroll-list";
import GroupedDocumentItem from "@/components/search/results/list/item/GroupedDocumentItem";
import GroupItem from "@/ecm/models/search/GroupItem";

export default {
  props: {
    searchType: {
      type: String,
      required: true,
    },
    searchPattern: {
      type: String,
      required: true,
    },
  },
  components: {
    DocumentListToolbar,
    DocumentItem,
    GroupedDocumentItem,
    GroupModal,
    FilterModal,
    SortModal,
    DistributionModal,
    ValidationModal,
    DocumentModal,
    MultipleEditionModal,
    AttachmentModal,
    FlowDiagramModal,
    SignatureModal,
    PluginLoaderModal,
    VueAutoVirtualScrollList,
  },
  created() {
    this.refreshResults();
  },
  data() {
    return {
      documentList: [],
      groupItems: [],
      filterItems: [],
      sortItems: [],
      loading: false,
      selectedDocuments: [],
      selectedDocument: -1,
      searchName: "",
      defaultVisibleFields: 4,
      groupModalKey: "groupModalKey",
      filterModalKey: "filterModalKey",
      sortModalKey: "sortModalKey",
      distributionModalKey: "distributionModalKey",
      validationModalKey: "validationModalKey",
      documentModalKey: "documentEditionModal",
      multipleEditionModalKey: "multipleEditionModal",
      attachmentModalKey: "attachmentModalDocList",
      flowDiagramModalKey: "flowDiagramModalKey",
      signatureModalKey: "signatureModal",
      pluginLoaderModalKey: "pluginLoaderModalKey",
    };
  },
  computed: {
    signatureModalInstance() {
      return this.$refs[this.signatureModalKey];
    },
    attachmentModalInstance() {
      return this.$refs[this.attachmentModalKey];
    },
    multipleEditionModalInstance() {
      return this.$refs[this.multipleEditionModalKey];
    },
    documentModalInstance() {
      return this.$refs[this.documentModalKey];
    },
    styleMaxHeight() {
      return `max-height: ${this.resultMaxHeight}px`;
    },
    resultMaxHeight() {
      return window.innerHeight - 75 - 65 - 58 - 43; //75px header, 65px footer, 58px toolbar, 43px search name
    },
    groupModalInstance() {
      return this.$refs[this.groupModalKey];
    },
    filterModalInstance() {
      return this.$refs[this.filterModalKey];
    },
    sortModalInstance() {
      return this.$refs[this.sortModalKey];
    },
    distributionModalInstance() {
      return this.$refs[this.distributionModalKey];
    },
    validationModalInstance() {
      return this.$refs[this.validationModalKey];
    },
    flowDiagramModalInstance() {
      return this.$refs[this.flowDiagramModalKey];
    },
    pluginLoaderModalInstance() {
      return this.$refs[this.pluginLoaderModalKey];
    },

    fieldList() {
      return Array.prototype.concat.apply(
        [],
        this.documentList.map((f) => f.Fields)
      );
    },
    filteredFieldList() {
      return Array.prototype.concat.apply(
        [],
        this.filteredSortedDocumentList.map((f) => f.Fields)
      );
    },
    filteredSortedDocumentList() {
      var filteredSortedList = [...this.documentList];
      //FILTER
      if (this.filterItems.length > 0) {
        filteredSortedList = [];
        var originalList = this.documentList;
        this.filterItems.forEach((filter) => {
          var filteredDocuments = originalList.filter((d) =>
            d.Fields.some(
              (f) =>
                f.Title == filter.fieldTitle &&
                filter.fieldValues.includes(f.Value)
            )
          );
          filteredDocuments.forEach((fdoc) => {
            if (!filteredSortedList.some((d) => d.ObjectID == fdoc.ObjectID)) {
              filteredSortedList.push(fdoc);
            }
          });
        });
      }
      var userDecSep = this.$store.getters["account/user"].MetricDecimalSep;
      var userThouSep = this.$store.getters["account/user"].MetricThousandSep;
      var userFormat = this.$store.getters["account/user"].MetricDateFormat;

      //SORT
      function compareDocuments(fieldTitle, order = "asc") {
        return function innerSort(a, b) {
          var fieldA = a.Fields.find((f) => f.Title === fieldTitle);
          var fieldB = b.Fields.find((f) => f.Title === fieldTitle);
          if (!fieldA) {
            // field doesn't exist
            return 1;
          }
          if (!fieldB) {
            // field doesn't exist
            return -1;
          }

          var varA =
            typeof fieldA.Value === "string"
              ? fieldA.Value.toUpperCase()
              : fieldA.Value;
          var varB =
            typeof fieldB.Value === "string"
              ? fieldB.Value.toUpperCase()
              : fieldB.Value;

          if (fieldA.Type === FieldType.DateTime) {
            if (fieldA.Code == StandardFieldCode.CreationDate) {
              userFormat = `${userFormat} HH:mm`;
            }
            varA = Utils.date.fromUserFormat(varA, userFormat);
            varB = Utils.date.fromUserFormat(varB, userFormat);
          }
          if (fieldA.Type === FieldType.Numeric) {
            varA = Utils.number.fromUserFormat(varA, userDecSep, userThouSep);
            varB = Utils.number.fromUserFormat(varB, userDecSep, userThouSep);
          }

          let comparison = 0;
          if (varA > varB) {
            comparison = 1;
          } else if (varA < varB) {
            comparison = -1;
          }
          return order === "desc" ? comparison * -1 : comparison;
        };
      }
      this.sortItems.forEach((sort) => {
        filteredSortedList.sort(
          compareDocuments(sort.fieldTitle, sort.sortOrder)
        );
      });
      return filteredSortedList;
    },
    groupedDocumentList() {
      if (!this.groupItems || this.groupItems.length == 0) return null;
      var groupedItems = groupDocuments(
        this.groupItems,
        0,
        this.filteredSortedDocumentList
      );

      //NESTED GROUP
      function groupDocuments(groupItems, groupLevel, documentsToGroup) {
        const groupItem = groupItems[groupLevel];
        const childGroupItem = groupItems[groupLevel + 1];

        var fieldValues = Array.from(
          new Set(
            documentsToGroup
              .flatMap((f) => f.Fields)
              .filter((f) => f.Title == groupItem.fieldTitle)
              .map((f) => f.Value)
          )
        );

        var grouped = fieldValues.map((fv) => {
          var groupedDocuments = documentsToGroup.filter((d) =>
            d.Fields.some(
              (df) => df.Title == groupItem.fieldTitle && df.Value == fv
            )
          );

          return {
            title: `${groupItem.fieldTitle}: ${fv}`,
            documents: groupedDocuments,
            children: childGroupItem
              ? groupDocuments(groupItems, groupLevel + 1, groupedDocuments)
              : null,
            sumFields: groupItem.sumFields,
            maxFields: groupItem.maxFields,
            minFields: groupItem.minFields,
            avgFields: groupItem.avgFields,
            summarizeOnlySelectedDocuments:
              groupItem.summarizeOnlySelectedDocuments,
          };
        });
        return grouped;
      }
      return groupedItems;
    },
    ungroupedDocumentList() {
      if (!this.groupedDocumentList || this.groupedDocumentList.length == 0) {
        return this.filteredSortedDocumentList;
      } else {
        return this.filteredSortedDocumentList.filter(
          (d) =>
            !this.groupedDocumentList.some((gi) =>
              gi.documents.some((gid) => gid.ObjectID == d.ObjectID)
            )
        );
      }
    },
  },
  mounted() {
    EventBus.$on("refreshDocumentList", () => {
      this.refreshResults();
    });
    EventBus.$on("dragAndDrop", (data) => {
      this.showDragAndDropModal(data);
    });
    EventBus.$on("selectDocument", (documentId) => {
      this.selectedDocument = documentId;
    });
  },
  destroyed() {
    EventBus.$off("refreshDocumentList");
    EventBus.$off("dragAndDrop");
    EventBus.$off("selectDocument");
  },
  watch: {
    selectedDocument(val) {
      if (!val || val <= 0) {
        EventBus.$emit("cleanViewer");
      }
    },
  },
  methods: {
    onDocumentUpdated(document) {
      this.reloadDocument(document);
    },
    showDragAndDropModal(data) {
      this.$confirm(this.$t("DragAndDropConfirmation"), {
        title: this.$t("Confirm"),
        icon: "mdi-attachment",
        color: "warning",
        width: 400,
        buttonTrueText: this.$t("Clone"),
        buttonFalseText: this.$t("AddAttachment"),
      }).then((res) => {
        if (res == 1)
          this.documentModalInstance.clone(data.documentId, data.files);
        else if (res == 2)
          this.attachmentModalInstance.new(data.documentId, data.files);
      });
    },
    onAttachmentSaved() {
      this.refreshResults();
    },
    editDocument(documentId) {
      this.documentModalInstance.edit(documentId);
    },
    showFlowDiagram(documentId) {
      DocumentService.getFlowDiagrams(documentId).then((result) => {
        this.flowDiagramModalInstance.fromDocumentList(result);
      });
    },
    signDocument(documentId) {
      this.signatureModalInstance.showModal(documentId);
    },
    onDocumentSelected({ isSelected, documentId } = value) {
      if (isSelected) {
        this.selectedDocuments.push(documentId);
      } else {
        this.selectedDocuments = this.selectedDocuments.filter(
          (id) => id != documentId
        );
      }
    },
    reloadDocument({ oldObjectID, newObjectID, searchAllVersions } = value) {
      if (newObjectID) {
        var searchRequest = {
          SearchType: SearchType.DocumentIDs,
          SearchPattern: newObjectID,
          SearchAllVersions: searchAllVersions == false ? false : true,
        };
        SearchService.getSearchResults(searchRequest).then((data) => {
          if (data.Results.length > 0) {
            var newDoc = data.Results[0];
            this.addStandardFields(newDoc);
            this.documentList = this.documentList.map(function (doc) {
              return doc.ObjectID == oldObjectID ? newDoc : doc;
            });
            this.loadPdf(newDoc.ObjectID);
          } else {
            Utils.toast.error("Unauthorized");
          }
        });
      } else if (oldObjectID) {
        this.removeDocumentFromList(oldObjectID);
      }
      //ALWAYS
      if (oldObjectID) {
        this.onDocumentSelected({ isSelected: false, documentId: oldObjectID });
      }
    },
    loadPdf(documentId) {
      this.selectedDocument = documentId;
      //CHECK READ PERMISSIONS
      var doc = this.documentList.find((d) => d.ObjectID == documentId);
      if (doc.Permissions.DocumentRead) {
        if (this.searchType == SearchType.FullText) {
          var pattern = JSON.parse(this.searchPattern);
          EventBus.$emit(
            "loadDocument-with-search",
            documentId,
            pattern.searchPattern
          );
        } else {
          EventBus.$emit("loadDocument", documentId);
        }
      } else {
        EventBus.$emit("cleanViewer");
      }
    },
    onSign({ oldObjectID, newObjectID } = value) {
      this.reloadDocument({
        oldObjectID: oldObjectID,
        newObjectID: newObjectID,
      });
    },
    onDocumentBlocked(objectID) {
      this.reloadDocument({ oldObjectID: objectID, newObjectID: objectID });
    },
    onReceptionConfirmed(objectID) {
      if (this.searchType == SearchType.Distribution) {
        this.refreshResults();
      } else {
        this.reloadDocument({ oldObjectID: objectID, newObjectID: objectID });
      }
    },
    onDistributionEnded(objectID) {
      if (this.searchType == SearchType.Distribution) {
        this.refreshResults();
      } else {
        this.reloadDocument({ oldObjectID: objectID, newObjectID: objectID });
      }
    },
    onVersionDeleted({ oldObjectID, newObjectID } = value) {
      this.reloadDocument({
        oldObjectID: oldObjectID,
        newObjectID: newObjectID,
      });
    },

    loadPlugin({ plugin, objectIDs } = value) {
      var documents = this.documentList.filter((document) =>
        objectIDs.includes(document.ObjectID)
      );
      this.pluginLoaderModalInstance.load(plugin, documents);
    },
    selectAllDocuments(isSelected) {
      if (isSelected) {
        this.selectedDocuments = this.filteredSortedDocumentList.map(
          (d) => d.ObjectID
        );
      } else {
        this.selectedDocuments = [];
      }
    },
    removeDocumentFromList(documentId) {
      if (documentId == this.selectedDocument) {
        this.selectedDocument = -1;
      }
      this.documentList = this.documentList.filter(
        (document) => document.ObjectID != documentId
      );
      this.onDocumentSelected({ isSelected: false, documentId: documentId });
    },
    refreshResults(objectIDs) {
      if (objectIDs) {
        for (let i = 0; i < objectIDs.length; i++) {
          this.reloadDocument({
            oldObjectID: objectIDs[i],
            newObjectID: objectIDs[i],
            searchAllVersions: false,
          });
        }
      } else {
        this.selectAllDocuments(false);
        this.loading = true;
        var searchRequest = {
          SearchType: this.searchType,
          SearchPattern: this.searchPattern,
          ContentTypeIDs: "",
          SearchAllVersions: false,
          OrderByQueryPosition: false,
          AllowDeleted: false,
          FieldCodes: "",
        };
        if (
          this.searchType == SearchType.FullText ||
          this.searchType == SearchType.AdvancedSearch ||
          this.searchType == SearchType.DocumentIDs ||
          this.searchType == SearchType.Alert
        ) {
          try {
            var pattern = JSON.parse(this.searchPattern);
            if (pattern.searchPattern)
              searchRequest.SearchPattern = pattern.searchPattern;
            if (pattern.contentTypeIDs)
              searchRequest.ContentTypeIDs = pattern.contentTypeIDs;
            if (pattern.searchAllVersions)
              searchRequest.SearchAllVersions = pattern.searchAllVersions;
            if (pattern.orderByQueryPosition)
              searchRequest.OrderByQueryPosition = pattern.orderByQueryPosition;
            if (pattern.fieldCodes)
              searchRequest.FieldCodes = pattern.fieldCodes;
            if (pattern.allowDeleted)
              searchRequest.AllowDeleted = pattern.allowDeleted;
          } catch {}
        }
        SearchService.getSearchResults(searchRequest).then((data) => {
          this.searchName = data.SearchName;
          this.defaultVisibleFields = data.DefaultVisibleFields;
          this.loadDocuments(data.Results);
        });
      }
    },
    loadDocuments(results) {
      results.forEach((document) => {
        this.addStandardFields(document);
      });
      this.documentList = results;
      this.loading = false;
      if (results.length > 0) {
        this.loadPdf(results[0].ObjectID);
        if (this.selectedDocument == -1)
          this.selectedDocument = results[0].ObjectID;
      } else {
        EventBus.$emit("cleanViewer");
      }
    },
    addStandardFields(document) {
      var standardFields = this.$store.getters["account/searchStandardFields"];
      standardFields.forEach((sf) => {
        var fieldCode = sf.Code;
        var fieldTitle = sf.Title;
        var fieldType = sf.Type;
        var fieldValue = "";
        switch (sf.Code) {
          case StandardFieldCode.CreationDate:
            fieldValue = document.CreationDate;
            break;
          case StandardFieldCode.Author:
            fieldValue = document.Author;
            break;
          case StandardFieldCode.DocumentId:
            fieldValue = document.ObjectID;
            break;
        }
        if (fieldValue != "") {
          document.Fields.push({
            Title: fieldTitle,
            Code: fieldCode,
            Type: fieldType,
            Value: fieldValue,
            IsVisible: true,
          });
        }
      });
    },
    onValidate() {
      if (this.selectedDocuments.length == 0) {
        Utils.toast.warning(this.$t("DocumentSelectionError"));
        return;
      }
      this.$confirm(this.$t("DocumentsValidationConfirm"), {
        title: this.$t("Validate"),
        icon: "mdi-warning",
        color: "green",
        buttonTrueText: this.$t("Validate"),
        buttonFalseText: this.$t("Cancel"),
      }).then((res) => {
        if (res == 1) {
          var selectedDocuments = this.documentList.filter((d) =>
            this.selectedDocuments.includes(d.ObjectID)
          );
          this.validationModalInstance.new(selectedDocuments, "V");
        }
      });
    },
    onRefuse() {
      if (this.selectedDocuments.length == 0) {
        Utils.toast.warning(this.$t("DocumentSelectionError"));
        return;
      }

      this.$confirm(this.$t("DocumentsRefuseConfirm"), {
        title: this.$t("Refuse"),
        icon: "mdi-warning",
        color: "red",
        buttonTrueText: this.$t("Refuse"),
        buttonFalseText: this.$t("Cancel"),
      }).then((res) => {
        if (res == 1) {
          var selectedDocuments = this.documentList.filter((d) =>
            this.selectedDocuments.includes(d.ObjectID)
          );
          this.validationModalInstance.new(selectedDocuments, "R");
        }
      });
    },
    onGroup() {
      if (this.groupItems.length > 0) {
        let cloned = this.groupItems.map(item => {
          return Utils.object.clone(new GroupItem(), item)
        });

        this.groupModalInstance.show(cloned);
      }
      else {
        this.groupModalInstance.show();
      }
    },
    onGrouped(groupItems) {
      this.groupItems = groupItems;
    },
    onFilter() {
      this.filterModalInstance.show();
    },
    onFiltered(filterItems) {
      this.filterItems = filterItems;
    },
    onSort() {
      this.sortModalInstance.show();
    },
    onSorted(sortItems) {
      this.sortItems = sortItems;
    },
    onInternalDistributeSelected() {
      if (this.selectedDocuments.length == 0) {
        Utils.toast.warning(this.$t("DocumentSelectionError"));
        return;
      }
      var documents = this.documentList.filter((document) =>
        this.selectedDocuments.includes(document.ObjectID)
      );
      this.distributionModalInstance.new(documents, true);
    },
    onExternalDistributeSelected() {
      if (this.selectedDocuments.length == 0) {
        Utils.toast.warning(this.$t("DocumentSelectionError"));
        return;
      }
      //ExternalDistributionNotAllowed
      var notAllowed = this.documentList
        .filter(
          (document) =>
            this.selectedDocuments.includes(document.ObjectID) &&
            !document.Permissions.DocumentExternalDistribution
        )
        .map(function (document) {
          return document.ObjectID;
        })
        .join(",");

      if (notAllowed) {
        var message = this.$t("ExternalDistributionNotAllowed").replace(
          "{0}",
          notAllowed
        );
        Utils.toast.error(message);
      } else {
        var documents = this.documentList.filter((document) =>
          this.selectedDocuments.includes(document.ObjectID)
        );
        this.distributionModalInstance.new(documents, false);
      }
    },
    onExportSelected() {
      if (this.selectedDocuments.length == 0) {
        Utils.toast.warning(this.$t("DocumentSelectionError"));
        return;
      }
      DocumentService.export(this.selectedDocuments);
    },
    onDeleteSelected() {
      if (this.selectedDocuments.length == 0) {
        Utils.toast.warning(this.$t("DocumentSelectionError"));
        return;
      }
      this.$confirm(this.$t("DeleteDocumentsConfirmation"), {
        title: this.$t("Delete"),
        icon: "mdi-warning",
        color: "warning",
        buttonTrueText: this.$t("Delete"),
        buttonFalseText: this.$t("Cancel"),
      }).then((res) => {
        if (res == 1) {
          DocumentService.remove(this.selectedDocuments).then((result) => {
            Utils.toast.success(this.$t("DocumentsDeletedSuccessfully"));
            this.selectedDocuments.forEach((documentId) => {
              this.removeDocumentFromList(documentId);
            });

            this.selectedDocuments = [];
          });
        }
      });
    },
    onDownloadSelected() {
      if (this.selectedDocuments.length == 0) {
        Utils.toast.warning(this.$t("DocumentSelectionError"));
        return;
      }
      var documentsToDownload = this.documentList
        .filter(
          (document) =>
            this.selectedDocuments.includes(document.ObjectID) &&
            document.Permissions.DocumentDownload
        )
        .map((d) => d.ObjectID);

      if (documentsToDownload.length == 0) {
        Utils.toast.error(this.$t("DocumentsNotAllowedToDownload"));
        return;
      }
      DocumentService.downloadSelected(documentsToDownload);
    },
    onEditSelected() {
      if (this.selectedDocuments.length == 0) {
        Utils.toast.warning(this.$t("DocumentSelectionError"));
        return;
      }

      var documents = this.documentList.filter(
        (document) =>
          this.selectedDocuments.filter((id) => id == document.ObjectID)
            .length > 0
      );
      var contentTypeList = [];
      documents.forEach((document) => {
        var exist =
          contentTypeList.filter(
            (contentType) => contentType.id == document.ContentTypeID
          ).length > 0;
        if (!exist) {
          contentTypeList.push({
            id: document.ContentTypeID,
            name: document.ContentType,
            loading: false,
            document: null,
          });
        }
      });

      var documentList = documents.map(function (document) {
        return { id: document.ObjectID, contentTypeId: document.ContentTypeID };
      });

      this.multipleEditionModalInstance.showModal(
        documentList,
        contentTypeList
      );
    },
  },
};
</script>